// ─── NetworkGraphPage.jsx ─────────────────────────────────────────────────────
// Relationship / Network Graph — investigate entity connections across the platform.
// Hub-and-spoke layout using pure inline-styled divs with trig positioning.

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { db } from "./dbClient";
import { useTheme } from "./theme";

const GRAPH_SIZE = 500;
const CENTER     = GRAPH_SIZE / 2;
const RADIUS     = 185;

function polarToXY(angleDeg, r) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CENTER + r * Math.cos(rad),
    y: CENTER + r * Math.sin(rad),
  };
}

function NodeCircle({ label, subLabel, x, y, isCenter, color, C, onClick, selected }) {
  const size  = isCenter ? 64 : 52;
  const half  = size / 2;
  return (
    <div
      onClick={onClick}
      title={label}
      style={{
        position: "absolute",
        left: x - half,
        top:  y - half,
        width: size,
        height: size,
        borderRadius: "50%",
        background: isCenter
          ? `radial-gradient(circle, ${color}33, ${color}11)`
          : selected
          ? `radial-gradient(circle, ${C.accent}33, ${C.accent}11)`
          : C.surfaceAlt,
        border: `2px solid ${selected ? C.accent : isCenter ? color : C.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s, background 0.15s",
        boxShadow: isCenter
          ? `0 0 16px ${color}44`
          : selected
          ? `0 0 12px ${C.accent}44`
          : "none",
        zIndex: 2,
      }}>
      <div style={{
        color: isCenter ? color : C.text,
        fontSize: isCenter ? 10 : 9,
        fontFamily: "'DM Mono', monospace",
        fontWeight: 700,
        textAlign: "center",
        padding: "0 4px",
        overflow: "hidden",
        wordBreak: "break-word",
        lineHeight: 1.3,
        maxHeight: size - 14,
      }}>
        {label.length > 12 ? label.slice(0, 11) + "…" : label}
      </div>
      {subLabel && (
        <div style={{ color: C.textDim, fontSize: 8, marginTop: 1, textAlign: "center" }}>
          {subLabel}
        </div>
      )}
    </div>
  );
}

function EdgeLine({ x1, y1, x2, y2, label, C }) {
  // SVG line drawn as absolutely positioned element within the graph container
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle  = Math.atan2(dy, dx) * (180 / Math.PI);
  const midX   = (x1 + x2) / 2;
  const midY   = (y1 + y2) / 2;

  return (
    <>
      {/* Line */}
      <div style={{
        position: "absolute",
        left: x1,
        top:  y1,
        width: length,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${C.border}, transparent)`,
        transformOrigin: "0 50%",
        transform: `rotate(${angle}deg)`,
        zIndex: 1,
      }} />
      {/* Edge label */}
      <div style={{
        position: "absolute",
        left: midX - 36,
        top:  midY - 9,
        width: 72,
        textAlign: "center",
        color: C.textDim,
        fontSize: 8,
        fontFamily: "'DM Mono', monospace",
        background: C.bg,
        borderRadius: 3,
        padding: "1px 3px",
        pointerEvents: "none",
        zIndex: 3,
        letterSpacing: "0.04em",
      }}>
        {label}
      </div>
    </>
  );
}

function customerDisplayName(c) {
  return c.business_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "—";
}

export default function NetworkGraphPage({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();

  const [query, setQuery]                   = useState("");
  const [walletInput, setWalletInput]       = useState("");
  const [searching, setSearching]           = useState(false);
  const [centerNode, setCenterNode]         = useState(null);
  const [nodes, setNodes]                   = useState([]);
  const [edges, setEdges]                   = useState([]);
  const [selected, setSelected]             = useState(null);
  const [error, setError]                   = useState(null);
  const [allCustomers, setAllCustomers]     = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [mode, setMode]                     = useState("customer"); // "customer" or "wallet"
  const [selectedNetwork, setSelectedNetwork] = useState("ethereum");

  // Load all approved customers for the browser panel
  useEffect(() => {
    if (!company?.id) return;
    setLoadingCustomers(true);
    (async () => {
      try {
        const { data } = await db.from("kyc_submissions")
          .select("id, first_name, last_name, business_name, email, phone, address_line1, city, country, type, risk_rating")
          .eq("company_id", company.id)
          .eq("status", "approved")
          .order("first_name", true)
          .limit(150)
          .execute();
        setAllCustomers(data || []);
      } catch (err) {
        console.error("[Network Graph] Load customers error:", err.message);
      } finally {
        setLoadingCustomers(false);
      }
    })();
  }, [company?.id]);

  async function runWalletAnalysis(walletIds, selectedNetwork = "ethereum") {
    if (!walletIds || walletIds.length === 0) return;
    setSearching(true);
    setError(null);
    setCenterNode(null);
    setNodes([]);
    setEdges([]);
    setSelected(null);

    try {
      // Helper to detect address type
      const detectAddressType = (address) => {
        const addr = address.toLowerCase().trim();
        if (addr.match(/^(bc1|tb1)/)) return 'bitcoin'; // Segwit
        if (addr.match(/^[13]/)) return 'bitcoin'; // Legacy
        if (addr.match(/^0x[a-f0-9]{40}$/)) return 'ethereum'; // EVM
        return null;
      };

      // API config for blockchain explorers
      const EXPLORER_CONFIG = {
        ethereum: { chainId: 1, v2Url: 'https://api.etherscan.io/v2/api', type: 'evm' },
        binance: { chainId: 56, v2Url: 'https://api.bscscan.com/v2/api', type: 'evm' },
        polygon: { chainId: 137, v2Url: 'https://api.polygonscan.com/v2/api', type: 'evm' },
        bitcoin: { chainId: 0, baseUrl: 'https://blockchair.com/bitcoin/api/v1', type: 'bitcoin' }
      };

      // Detect network from addresses if not specified, or use selected network
      const normalizedWallets = walletIds.map(w => w.toLowerCase().trim());
      const detectedTypes = normalizedWallets.map(w => detectAddressType(w));
      const hasBitcoin = detectedTypes.includes('bitcoin');
      const hasEVM = detectedTypes.some(t => t && t !== 'bitcoin');

      let network = selectedNetwork;
      if (hasBitcoin && !hasEVM) network = 'bitcoin';
      else if (!selectedNetwork || selectedNetwork === 'ethereum') network = 'ethereum';

      const config = EXPLORER_CONFIG[network];
      const etherscanKey = import.meta.env.VITE_ETHERSCAN_KEY || '';
      const blockchairKey = import.meta.env.VITE_BLOCKCHAIR_KEY || '';

      const walletSet = new Set(normalizedWallets);

      const foundNodes = [];
      const foundEdges = [];
      const txsByPair = {}; // Track transactions between wallet pairs
      const walletTxCounts = {}; // Track transaction counts per wallet

      // 1. Fetch transactions for each wallet (calling V2 API directly)
      console.log(`[NetworkGraph] Starting analysis for wallets:`, normalizedWallets);
      console.log(`[NetworkGraph] Wallet set keys:`, Array.from(walletSet));

      for (const wallet of normalizedWallets) {
        try {
          let url, data, transactions = [];

          if (config.type === 'bitcoin') {
            // Bitcoin API - use blockchain handler
            url = `/api/blockchain/bitcoin?address=${encodeURIComponent(wallet)}`;
            console.log(`[NetworkGraph] Fetching Bitcoin transactions for ${wallet}`);
            const res = await fetch(url, {
              signal: AbortSignal.timeout(15000),
              headers: { "Content-Type": "application/json" }
            });

            if (!res.ok) {
              console.warn(`Failed to fetch Bitcoin transactions for ${wallet}: HTTP ${res.status}`);
              continue;
            }

            data = await res.json();
            // Extract transactions from blockchain handler response
            // Format: {status: '1', message: 'OK', result: [{from, to, value}, ...]}
            if (data.result && Array.isArray(data.result)) {
              transactions = data.result.map(tx => ({
                from: (tx.from || "").toLowerCase(),
                to: (tx.to || "").toLowerCase(),
                value: tx.value // Keep in satoshis
              }));
            }
          } else {
            // EVM chains - use Etherscan V2 API
            url = `${config.v2Url}?chainid=${config.chainId}&module=account&action=txlist&address=${wallet}&startblock=0&endblock=99999999&sort=desc&page=1&offset=100&apikey=${etherscanKey}`;
            console.log(`[NetworkGraph] Fetching EVM transactions for ${wallet}`);
            const res = await fetch(url, {
              signal: AbortSignal.timeout(15000),
              headers: { "Content-Type": "application/json" }
            });

            if (!res.ok) {
              console.warn(`Failed to fetch transactions for ${wallet}: HTTP ${res.status}`);
              continue;
            }

            data = await res.json();
            transactions = Array.isArray(data.result) ? data.result : [];
          }

          console.log(`[NetworkGraph] Got ${transactions.length} transactions for ${wallet}`);
          walletTxCounts[wallet] = transactions.length;

          // 2. Find transactions between the provided wallets or involving them
          transactions.forEach((tx, idx) => {
            const from = (tx.from || "").toLowerCase();
            const to = (tx.to || "").toLowerCase();

            // Count transactions where:
            // - Both parties are in our wallet list (direct connection), OR
            // - At least one party is in our wallet list (wallet involvement)
            const fromInList = walletSet.has(from);
            const toInList = walletSet.has(to);

            if ((fromInList || toInList) && from !== to) {
              // For direct connections between wallets in the list
              if (fromInList && toInList) {
                const pairKey = [from, to].sort().join("||");
                if (!txsByPair[pairKey]) {
                  txsByPair[pairKey] = { from, to, count: 0, totalValue: 0 };
                }
                txsByPair[pairKey].count++;
                txsByPair[pairKey].totalValue += parseFloat(tx.value || 0) / (config.type === 'bitcoin' ? 1 : 1e18);
              } else {
                // For wallet → external address or external → wallet
                const otherAddr = fromInList ? to : from;
                const pairKey = [wallet.toLowerCase(), otherAddr].sort().join("||");
                if (!txsByPair[pairKey]) {
                  txsByPair[pairKey] = {
                    from: fromInList ? wallet.toLowerCase() : otherAddr,
                    to: toInList ? wallet.toLowerCase() : otherAddr,
                    count: 0,
                    totalValue: 0
                  };
                }
                txsByPair[pairKey].count++;
                txsByPair[pairKey].totalValue += parseFloat(tx.value || 0) / (config.type === 'bitcoin' ? 1 : 1e18);
              }
            }
          });
        } catch (err) {
          console.warn(`Error fetching transactions for ${wallet}:`, err.message);
        }
      }
      console.log(`[NetworkGraph] Final txsByPair:`, txsByPair);

      // 3. Create wallet nodes
      normalizedWallets.forEach(wallet => {
        const txCount = walletTxCounts[wallet] || 0;
        foundNodes.push({
          id: wallet,
          label: wallet.slice(0, 10) + "...",
          type: "wallet",
          fullWallet: wallet,
          detail: {
            "wallet": wallet,
            "total transactions": txCount
          }
        });
      });

      // 4. Create edges for transactions between wallets
      Object.values(txsByPair).forEach(pair => {
        foundEdges.push({
          from: pair.from,
          to: pair.to,
          label: `${pair.count} txs`,
          count: pair.count,
          value: pair.totalValue
        });
      });

      if (foundEdges.length === 0) {
        setError(`No transactions found between the provided wallets on ${selectedNetwork}.`);
        setSearching(false);
        return;
      }

      setCenterNode({
        id: "wallet_group",
        label: `${walletIds.length} Wallets`,
        type: "wallet-group"
      });
      setNodes(foundNodes);
      setEdges(foundEdges);
    } catch (err) {
      setError("Blockchain analysis failed: " + (err.message || "unknown error"));
    }
    setSearching(false);
  }

  async function runSearch(q) {
    if (!q || !company?.id) return;
    setSearching(true);
    setError(null);
    setCenterNode(null);
    setNodes([]);
    setEdges([]);
    setSelected(null);

    try {
      // 1. Find the anchor customer from kyc_submissions (approved)
      // Build OR across full name and individual words (handles "Nathan Spinder" matching first+last)
      // Get all approved customers and filter client-side for flexibility
      const { data: allApproved } = await db.from("kyc_submissions")
        .select("id, first_name, last_name, business_name, email, phone, address_line1, city, country, type, nationality, source_of_funds, pep_declaration, risk_rating")
        .eq("company_id", company.id)
        .eq("status", "approved")
        .execute();

      if (!allApproved || allApproved.length === 0) {
        setError(`No approved customers found.`);
        setSearching(false);
        return;
      }

      // Filter by search query (case-insensitive)
      const qLower = q.toLowerCase();
      const words = q.split(/\s+/).filter(Boolean);
      const anchors = allApproved.filter(c => {
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        const business = (c.business_name || "").toLowerCase();
        const email = (c.email || "").toLowerCase();
        return fullName.includes(qLower) || business.includes(qLower) || email.includes(qLower) ||
               words.some(w => fullName.includes(w.toLowerCase()) || business.includes(w.toLowerCase()));
      });

      if (!anchors || anchors.length === 0) {
        setError(`No approved customer found matching "${q}".`);
        setSearching(false);
        return;
      }

      const anchor = anchors[0];
      const anchorName = customerDisplayName(anchor);
      setCenterNode({ id: anchor.id, label: anchorName, type: "customer" });

      const foundNodes = [];
      const foundEdges = [];
      const seen = new Set([anchor.id]);

      // 2. Same address matches (from kyc_submissions)
      if (anchor.address_line1) {
        const sameAddr = allApproved.filter(c =>
          c.id !== anchor.id &&
          c.address_line1 &&
          c.address_line1.toLowerCase().includes(anchor.address_line1.toLowerCase())
        ).slice(0, 10);

        (sameAddr || []).forEach(c => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            foundNodes.push({ id: c.id, label: customerDisplayName(c), type: "customer", detail: { email: c.email, phone: c.phone, city: c.city, country: c.country } });
            foundEdges.push({ from: anchor.id, to: c.id, label: "same address" });
          }
        });
      }

      // 3. Same phone
      if (anchor.phone) {
        const samePhone = allApproved.filter(c =>
          c.id !== anchor.id && c.phone === anchor.phone
        ).slice(0, 10);

        (samePhone || []).forEach(c => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            foundNodes.push({ id: c.id, label: customerDisplayName(c), type: "customer", detail: { email: c.email, phone: c.phone } });
            foundEdges.push({ from: anchor.id, to: c.id, label: "same phone" });
          } else {
            foundEdges.push({ from: anchor.id, to: c.id, label: "same phone" });
          }
        });
      }

      // 4. Same email domain (non-public domains only)
      const emailDomain = anchor.email?.split("@")[1];
      if (emailDomain && !["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com"].includes(emailDomain)) {
        const sameDomain = allApproved.filter(c =>
          c.id !== anchor.id && c.email && c.email.toLowerCase().endsWith(`@${emailDomain.toLowerCase()}`)
        ).slice(0, 10);

        (sameDomain || []).forEach(c => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            foundNodes.push({ id: c.id, label: customerDisplayName(c), type: "customer", detail: { email: c.email, phone: c.phone } });
            foundEdges.push({ from: anchor.id, to: c.id, label: "same email domain" });
          } else {
            foundEdges.push({ from: anchor.id, to: c.id, label: "same email domain" });
          }
        });
      }

      // 5. Same nationality / country
      if (anchor.nationality || anchor.country) {
        const nat = anchor.nationality || anchor.country;
        const sameNat = allApproved.filter(c =>
          c.id !== anchor.id && (c.nationality === nat || c.country === nat)
        ).slice(0, 8);

        (sameNat || []).forEach(c => {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            foundNodes.push({ id: c.id, label: customerDisplayName(c), type: "customer", detail: { email: c.email, nationality: c.nationality } });
            foundEdges.push({ from: anchor.id, to: c.id, label: "same nationality" });
          }
        });
      }

      // 6. Cases linked to same entity
      const { data: allCases } = await db.from("cases")
        .select("id, title, entity, linked_customer_id")
        .eq("company_id", company.id)
        .execute();

      const anchorCases = (allCases || []).filter(c =>
        (c.entity && c.entity.toLowerCase().includes(anchorName.toLowerCase())) ||
        c.linked_customer_id === anchor.id
      ).slice(0, 5);

      if (anchorCases && anchorCases.length > 0) {
        // Find other customers linked to same cases via entity name
        for (const c of anchorCases) {
          if (c.entity && c.entity.toLowerCase() !== anchorName.toLowerCase()) {
            const fakeId = `case_entity_${c.id}`;
            if (!seen.has(fakeId)) {
              seen.add(fakeId);
              foundNodes.push({
                id: fakeId,
                label: c.entity,
                type: "case",
                detail: { ...c, _type: "case" },
              });
              foundEdges.push({ from: anchor.id, to: fakeId, label: "linked case" });
            }
          }
        }
      }

      // Deduplicate edges by source+target pair (regardless of order)
      const seenEdges = new Set();
      const uniqueEdges = foundEdges.filter(e => {
        const key = [e.from, e.to].sort().join("||");
        if (seenEdges.has(key)) return false;
        seenEdges.add(key);
        return true;
      });

      setNodes(foundNodes);
      setEdges(uniqueEdges);
    } catch (err) {
      setError("Search failed: " + (err.message || "unknown error"));
    }
    setSearching(false);
  }

  // Build positioned graph nodes
  const graphNodes = [];
  if (centerNode) {
    graphNodes.push({ ...centerNode, x: CENTER, y: CENTER, isCenter: true });
    nodes.forEach((n, i) => {
      const angle = (360 / Math.max(nodes.length, 1)) * i - 90;
      const { x, y } = polarToXY(angle, RADIUS);
      graphNodes.push({ ...n, x, y, isCenter: false });
    });
  }

  const nodeById = {};
  graphNodes.forEach(n => { nodeById[n.id] = n; });

  const totalLinks = edges.length;
  const uniqueEntities = nodes.length;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", color: C.text,
          fontSize: 24, fontWeight: 700, margin: 0 }}>
          {t('network.title')}
        </h1>
        <div style={{ color: C.textDim, fontSize: 12, marginTop: 4 }}>
          {t('network.subtitle')}
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button
          onClick={() => { setMode("customer"); setWalletInput(""); setError(null); }}
          style={{
            background: mode === "customer" ? C.accent : "transparent",
            border: `1px solid ${mode === "customer" ? C.accent : C.border}`,
            borderRadius: 7,
            color: mode === "customer" ? C.bg : C.text,
            padding: "8px 16px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "'DM Mono', monospace",
            transition: "all 0.15s"
          }}>
          Customer Search
        </button>
        <button
          onClick={() => { setMode("wallet"); setQuery(""); setError(null); }}
          style={{
            background: mode === "wallet" ? C.accent : "transparent",
            border: `1px solid ${mode === "wallet" ? C.accent : C.border}`,
            borderRadius: 7,
            color: mode === "wallet" ? C.bg : C.text,
            padding: "8px 16px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "'DM Mono', monospace",
            transition: "all 0.15s"
          }}>
          Multi-Wallet Analysis
        </button>
      </div>

      {/* Search inputs */}
      {mode === "customer" ? (
        <div style={{ display: "flex", gap: 10, marginBottom: 24, alignItems: "center" }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && runSearch(query.trim())}
            placeholder="Enter customer name or entity to investigate..."
            style={{
              background: C.surfaceAlt,
              border: `1px solid ${C.border}`,
              borderRadius: 7,
              color: C.text,
              padding: "10px 14px",
              fontSize: 13,
              fontFamily: "'DM Mono', monospace",
              width: 420,
              outline: "none",
            }}
          />
          <button
            onClick={() => runSearch(query.trim())}
            disabled={searching || !query.trim()}
            style={{
              background: C.accent, border: "none", borderRadius: 7,
              color: C.bg, padding: "10px 24px", cursor: searching ? "not-allowed" : "pointer",
              fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace",
              opacity: searching || !query.trim() ? 0.6 : 1,
            }}>
            {searching ? "Searching..." : "Investigate"}
          </button>
          {centerNode && (
            <button
              onClick={() => { setCenterNode(null); setNodes([]); setEdges([]); setSelected(null); setQuery(""); }}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 7, color: C.textMid, padding: "10px 16px",
                cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
              Clear
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, marginBottom: 24, flexDirection: "column" }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono', monospace", minWidth: 80 }}>
              Network:
            </label>
            <select
              value={selectedNetwork}
              onChange={e => setSelectedNetwork(e.target.value)}
              style={{
                background: C.surfaceAlt,
                border: `1px solid ${C.border}`,
                borderRadius: 7,
                color: C.text,
                padding: "8px 12px",
                fontSize: 12,
                fontFamily: "'DM Mono', monospace",
                outline: "none",
                cursor: "pointer",
              }}>
              <option value="ethereum">Ethereum</option>
              <option value="binance">Binance Smart Chain</option>
              <option value="polygon">Polygon</option>
            </select>
          </div>
          <textarea
            value={walletInput}
            onChange={e => setWalletInput(e.target.value)}
            placeholder="Paste wallet addresses (comma or newline separated)&#10;Example: 0x1234567890123456789012345678901234567890"
            style={{
              background: C.surfaceAlt,
              border: `1px solid ${C.border}`,
              borderRadius: 7,
              color: C.text,
              padding: "12px 14px",
              fontSize: 13,
              fontFamily: "'DM Mono', monospace",
              width: "100%",
              maxWidth: 700,
              minHeight: 120,
              outline: "none",
              resize: "vertical",
            }}
          />
          <button
            onClick={() => {
              const wallets = walletInput
                .split(/[,\n]+/)
                .map(w => w.trim())
                .filter(Boolean);
              runWalletAnalysis(wallets, selectedNetwork);
            }}
            disabled={searching || !walletInput.trim()}
            style={{
              background: C.accent, border: "none", borderRadius: 7,
              color: C.bg, padding: "10px 24px", cursor: searching ? "not-allowed" : "pointer",
              fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace",
              opacity: searching || !walletInput.trim() ? 0.6 : 1,
              alignSelf: "flex-start",
            }}>
            {searching ? "Querying blockchain..." : "Analyze Transactions"}
          </button>
          {centerNode && (
            <button
              onClick={() => { setCenterNode(null); setNodes([]); setEdges([]); setSelected(null); setWalletInput(""); }}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                borderRadius: 7, color: C.textMid, padding: "10px 16px",
                cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono', monospace",
                alignSelf: "flex-start" }}>
              Clear
            </button>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: `${C.warn}15`, border: `1px solid ${C.warn}55`,
          borderRadius: 8, padding: "12px 16px", marginBottom: 20,
          color: C.warn, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
          {error}
        </div>
      )}

      {centerNode && (
        <div style={{ marginBottom: 14 }}>
          <span style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
            Connections found:{" "}
            <strong style={{ color: C.accent }}>{uniqueEntities} entities</strong>
            {", "}
            <strong style={{ color: C.accent }}>{totalLinks} links</strong>
          </span>
        </div>
      )}

      {/* Graph + side panel */}
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

        {/* Graph canvas */}
        {centerNode ? (
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 0,
            position: "relative",
            width: GRAPH_SIZE,
            height: GRAPH_SIZE,
            flexShrink: 0,
            overflow: "hidden",
          }}>
            {/* Grid dots background */}
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage: `radial-gradient(circle, ${C.border} 1px, transparent 1px)`,
              backgroundSize: "28px 28px",
              opacity: 0.5,
              pointerEvents: "none",
            }} />

            {/* Edges */}
            {edges.map((edge, i) => {
              const fromNode = nodeById[edge.from];
              const toNode   = nodeById[edge.to];
              if (!fromNode || !toNode) return null;
              return (
                <EdgeLine
                  key={i}
                  x1={fromNode.x} y1={fromNode.y}
                  x2={toNode.x}   y2={toNode.y}
                  label={edge.label}
                  C={C}
                />
              );
            })}

            {/* Nodes */}
            {graphNodes.map(node => (
              <NodeCircle
                key={node.id}
                label={node.label}
                subLabel={node.isCenter ? "SUBJECT" : node.type}
                x={node.x}
                y={node.y}
                isCenter={node.isCenter}
                color={node.isCenter ? C.accent
                  : node.type === "case" ? C.purple
                  : node.type === "wallet" ? "#a78bfa"
                  : C.success}
                C={C}
                selected={selected?.id === node.id}
                onClick={!node.isCenter ? () => setSelected(node) : undefined}
              />
            ))}

            {/* Legend */}
            <div style={{ position: "absolute", bottom: 12, left: 12,
              display: "flex", flexDirection: "column", gap: 5 }}>
              {[
                { color: C.accent,  label: "Subject" },
                { color: C.success, label: "Customer" },
                { color: "#a78bfa", label: "Wallet" },
                { color: C.purple,  label: "Case entity" },
              ].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%",
                    background: l.color, flexShrink: 0 }} />
                  <span style={{ color: C.textDim, fontSize: 9,
                    fontFamily: "'DM Mono', monospace" }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, width: GRAPH_SIZE, height: GRAPH_SIZE,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }}>◎</div>
              <div style={{ color: C.textDim, fontSize: 13,
                fontFamily: "'DM Mono', monospace" }}>
                Enter a name to build the network graph
              </div>
            </div>
          </div>
        )}

        {/* Side panel */}
        {selected && (
          <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Syne', sans-serif", color: C.text,
                fontSize: 16, fontWeight: 700, margin: 0 }}>
                {selected.label}
              </h3>
              <button
                onClick={() => setSelected(null)}
                style={{ background: "transparent", border: "none",
                  color: C.textDim, cursor: "pointer", fontSize: 18, padding: 0 }}>
                ×
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  background: selected.type === "case" ? `${C.purple}20` : `${C.success}20`,
                  color: selected.type === "case" ? C.purple : C.success,
                  border: `1px solid ${selected.type === "case" ? C.purple : C.success}55`,
                  borderRadius: 5, padding: "2px 9px", fontSize: 10, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.08em",
                }}>
                  {selected.type}
                </span>
              </div>

              {/* Connection reasons */}
              <div>
                <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em",
                  marginBottom: 8 }}>CONNECTION REASONS</div>
                {edges
                  .filter(e => e.to === selected.id || e.from === selected.id)
                  .map((e, i) => (
                    <div key={i} style={{
                      background: C.surfaceAlt, border: `1px solid ${C.border}`,
                      borderRadius: 5, padding: "5px 10px", marginBottom: 5,
                      color: C.textMid, fontSize: 11,
                      fontFamily: "'DM Mono', monospace",
                    }}>
                      {e.label}
                    </div>
                  ))}
              </div>

              {/* Entity details */}
              {selected.detail && (
                <div>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em",
                    marginBottom: 8 }}>DETAILS</div>
                  <div style={{ background: C.surfaceAlt, borderRadius: 8,
                    padding: 12, border: `1px solid ${C.border}` }}>
                    {Object.entries(selected.detail)
                      .filter(([k]) => !["id", "_type", "linked_customer_id"].includes(k))
                      .filter(([, v]) => v !== null && v !== undefined && v !== "")
                      .map(([k, v]) => (
                        <div key={k} style={{ display: "flex", gap: 8,
                          marginBottom: 6, alignItems: "flex-start" }}>
                          <span style={{ color: C.textDim, fontSize: 10,
                            minWidth: 90, flexShrink: 0, letterSpacing: "0.06em",
                            textTransform: "uppercase", paddingTop: 1 }}>
                            {k.replace(/_/g, " ")}
                          </span>
                          <span style={{ color: C.textMid, fontSize: 11,
                            wordBreak: "break-word" }}>
                            {String(v)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt to click a node */}
        {centerNode && !selected && nodes.length > 0 && (
          <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: 22, display: "flex", alignItems: "center",
            justifyContent: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.3 }}>←</div>
              <div style={{ color: C.textDim, fontSize: 12,
                fontFamily: "'DM Mono', monospace" }}>
                Click a connected node to see its details
              </div>
            </div>
          </div>
        )}

        {centerNode && nodes.length === 0 && (
          <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: 22, display: "flex", alignItems: "center",
            justifyContent: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: C.textDim, fontSize: 13,
                fontFamily: "'DM Mono', monospace" }}>
                No connections found for this entity.
              </div>
              <div style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>
                No shared addresses, phones, email domains, or linked cases detected.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Customer Browser */}
      {!centerNode && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <h2 style={{ fontFamily: "'Syne', sans-serif", color: C.text,
              fontSize: 15, fontWeight: 700, margin: 0 }}>
              Customer Browser
            </h2>
            <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
              — click any customer to start an investigation
            </span>
            {loadingCustomers && (
              <span style={{ color: C.accent, fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
                Loading…
              </span>
            )}
          </div>

          {allCustomers.length === 0 && !loadingCustomers && (
            <div style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono', monospace",
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
              padding: "20px 24px" }}>
              No approved customers found.
            </div>
          )}

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 10,
          }}>
            {allCustomers.map(c => {
              const name = customerDisplayName(c);
              const riskColor = c.risk_rating === "high"
                ? "#ef4444"
                : c.risk_rating === "medium"
                ? "#f59e0b"
                : "#22c55e";
              return (
                <div
                  key={c.id}
                  onClick={() => { setQuery(name); runSearch(name); }}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 9,
                    padding: "12px 14px",
                    cursor: "pointer",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.boxShadow = `0 0 0 1px ${C.accent}44`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 6 }}>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 700,
                      fontFamily: "'DM Mono', monospace", wordBreak: "break-word",
                      lineHeight: 1.3 }}>
                      {name}
                    </div>
                    {c.risk_rating && (
                      <div style={{ background: `${riskColor}20`, color: riskColor,
                        border: `1px solid ${riskColor}55`, borderRadius: 4,
                        padding: "1px 6px", fontSize: 9, fontWeight: 700,
                        textTransform: "uppercase", flexShrink: 0 }}>
                        {c.risk_rating}
                      </div>
                    )}
                  </div>
                  {c.email && (
                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 4,
                      fontFamily: "'DM Mono', monospace", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.email}
                    </div>
                  )}
                  {(c.city || c.country) && (
                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 2,
                      fontFamily: "'DM Mono', monospace" }}>
                      {[c.city, c.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                  <div style={{ color: C.accent, fontSize: 9, marginTop: 6,
                    fontFamily: "'DM Mono', monospace", opacity: 0.7 }}>
                    ◎ Investigate →
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
